import { memo } from "react";
import { type ConfiguratorProductData, type ProductData } from "~/domains/product/types/ProductData";
import NullableImage from "~/common/components/NullableImage";
import CenterContents from "~/common/components/CenterContents";
import { getPrimaryImage } from "~/utils/product/getPrimaryImage";
import { useProductDetailsUrl } from "~/features/PackageRestriction/hooks/useProductDetailsUrl";
import { showManufacturerLogo } from "~/utils/product/showManufacturerLogo";
import { ConfiguratorType } from "~/domains/configurator/types/ConfiguratorType";
import { useForcedConfiguratorTranslations } from "~/features/configurator/common/useXmlConfiguratorTranslations";
import { Stack, styled } from "~/common/mui";

const ProductImageRoot = styled("div")({ display: "flex" });

export interface ProductImageWidths {
  product: string;
  brand: string;
  manufacturer: string;
  gap: string;
}

interface Props {
  product: Pick<
    ProductData,
    "images" | "manufacturerLogoUrl" | "brandLogoUrl" | "url" | "isOptime" | "sapMaterialNumber" | "code"
  >;
  widths: ProductImageWidths;
  imageFormat?: string;
  clickable?: boolean;
}

/**
 * Represents an image of a product along with the necessary logos
 */
const ProductImage = ({
  product,
  widths: { product: productSize, brand, manufacturer, gap },
  imageFormat = "thumbnail",
  clickable = true,
  className,
  ...props
}: Props & JSX.IntrinsicElements["div"]) => {
  const { images, manufacturerLogoUrl, brandLogoUrl, isOptime } = product;

  let primaryImage = getPrimaryImage(images, { imageFormat });

  let configuratorType = ConfiguratorType.System;
  let useXml = false;
  if ("configuratorType" in product) {
    const configuratorProduct = product as ConfiguratorProductData;
    if (
      configuratorProduct.configuratorType === ConfiguratorType.InchShaft ||
      configuratorProduct.configuratorType === ConfiguratorType.MetricShaft
    ) {
      useXml = true;
      configuratorType = configuratorProduct.configuratorType;
    }
  }
  const xmlT = useForcedConfiguratorTranslations(product.code).t;
  if (useXml && primaryImage) {
    primaryImage = {
      ...primaryImage,
      url: xmlT?.(primaryImage.url) ?? primaryImage.url,
    };
  }

  let mainImage = (
    <CenterContents style={{ width: productSize, height: productSize, marginRight: gap }}>
      <NullableImage data={primaryImage} />
    </CenterContents>
  );

  const productUrl = useProductDetailsUrl(product);

  if (clickable && productUrl) {
    mainImage = <a href={productUrl}>{mainImage}</a>;
  }

  const manufacturerLogoVisible = showManufacturerLogo(manufacturerLogoUrl, isOptime);

  return (
    <ProductImageRoot className={className} {...props}>
      {mainImage}
      <Stack sx={{ minWidth: "32px" }}>
        {manufacturerLogoVisible && (
          <img style={{ width: manufacturer }} src={manufacturerLogoUrl} alt="Manufacturer logo" />
        )}
        {brandLogoUrl && <img style={{ width: brand, marginTop: "auto" }} src={brandLogoUrl} alt="Brand logo" />}
      </Stack>
    </ProductImageRoot>
  );
};
export default memo(ProductImage);
